import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import { DndContext, closestCenter, pointerWithin, PointerSensor, useSensor, useSensors, MeasuringStrategy } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThLarge, faSignOutAlt } from '@fortawesome/pro-light-svg-icons';
import './AdminNav.scss';

import AdminNavDrawer from './AdminNavDrawer';
import AdminNavShortcut from './AdminNavShortcut';
import AdminNavTrash from './AdminNavTrash';
import { NAV_TRASH_ID } from '../../constants/admin_nav_constants';
import { updateAdminNavPreferences } from '../../actions/userActions';
import {
  getVisibleNavItems,
  getActiveNavKey,
  keepVisibleKeys,
  togglePinnedKey,
  recordRecentKey,
  getPinnedAndRecentItems,
  getReorderedPinnedKeys,
  readLegacyPinnedKeys,
  clearLegacyLocalNavPrefs
} from '../../helpers/nav_helpers';

// Lock dragging to the horizontal axis so the single-row bar never appears to wrap.
const restrictToHorizontalAxis = ({ transform }) => ({ ...transform, y: 0 });

// The chip is X-locked to its row, so detect the trash by the cursor (it sits below the bar).
// Fall back to closest-center among the chips for sorting.
const collisionDetection = args => {
  const pointerHits = pointerWithin(args);
  const trashHit = pointerHits.find(hit => hit.id === NAV_TRASH_ID);
  if (trashHit) return [trashHit];
  return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter(c => c.id !== NAV_TRASH_ID) });
};

const TOP_BAR_RECENT_LIMIT = 6;

const AdminNav = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.users.currentUser);
  const scopes = currentUser?.scopes;
  const visibleItems = useMemo(() => getVisibleNavItems(scopes || []), [scopes]);
  const activeKey = getActiveNavKey(window.location.pathname);

  // Local, not on the Redux currentUser slice: App subscribes to currentUser, so writing here on every
  // change would re-render App and remount the routed page.
  const [pinnedKeys, setPinnedKeys] = useState(() => keepVisibleKeys(currentUser?.navPreferences?.pinned || [], visibleItems));
  const [recentKeys, setRecentKeys] = useState(() => keepVisibleKeys(currentUser?.navPreferences?.recent || [], visibleItems));

  const savePinned = pinned => {
    setPinnedKeys(pinned);
    dispatch(updateAdminNavPreferences({ pinned }));
  };
  const saveRecent = recent => {
    setRecentKeys(recent);
    dispatch(updateAdminNavPreferences({ recent }));
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const logoButtonRef = useRef(null);
  const migratedRef = useRef(false);

  // On touch devices a horizontal swipe is for scrolling the bar, so turn drag-to-reorder off there.
  const [isTouch, setIsTouch] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    logoButtonRef.current?.focus();
  };

  // Migrate any legacy localStorage pins into the account once.
  useEffect(() => {
    if (migratedRef.current || !currentUser?.id) return;
    migratedRef.current = true;
    const legacyPinned = readLegacyPinnedKeys();
    if (legacyPinned.length && !pinnedKeys.length) savePinned(keepVisibleKeys(legacyPinned, visibleItems));
    clearLegacyLocalNavPrefs();
  }, [currentUser?.id]);

  // Record the current page as most-recent so it pops into the top bar.
  useEffect(() => {
    if (currentUser?.id && activeKey && recentKeys[0] !== activeKey) {
      saveRecent(recordRecentKey(activeKey, recentKeys));
    }
  }, [activeKey, currentUser?.id]);

  // Lock body scroll + close on Escape while the drawer is open.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const onKeyDown = e => e.key === 'Escape' && closeDrawer();
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  // Track the coarse/fine pointer so a device switching input (or devtools emulation) updates live.
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const onChange = e => setIsTouch(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // An 8px activation distance lets a plain click navigate while a real drag reorders.
  // No sensors on touch → drag never starts, leaving the swipe to scroll the bar.
  const pointerSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const noSensors = useSensors();
  const sensors = isTouch ? noSensors : pointerSensors;

  const togglePin = key => savePinned(togglePinnedKey(key, pinnedKeys));

  // Pin every visible item in the original (pre-redesign) menu order — a quick fill for admins who miss the old bar.
  const restoreClassicMenu = () => {
    savePinned(visibleItems.map(item => item.key));
    window.ALERT.success('Restored the classic menu order.');
  };

  // Top bar shortcuts: pinned items first, then recents (auto-ordered by recency).
  const { pinnedItems, recentItems } = getPinnedAndRecentItems(pinnedKeys, recentKeys, visibleItems, TOP_BAR_RECENT_LIMIT);
  const shortcutItems = [...pinnedItems, ...recentItems];
  const shortcutKeys = shortcutItems.map(item => item.key);

  // Drop on the trash zone removes the item from the bar (unpins + drops from recents).
  const removeShortcut = key => {
    const nextPinned = pinnedKeys.filter(k => k !== key);
    const nextRecent = recentKeys.filter(k => k !== key);
    setPinnedKeys(nextPinned);
    setRecentKeys(nextRecent);
    dispatch(updateAdminNavPreferences({ pinned: nextPinned, recent: nextRecent }));
  };

  // Reorder the whole bar; the new order is persisted as pins so the drop position sticks.
  const reorderShortcuts = (activeId, overId) => savePinned(getReorderedPinnedKeys(shortcutKeys, activeId, overId, pinnedKeys));

  const handleDragEnd = ({ active, over }) => {
    setIsDragging(false);
    // A drag ends with a synthetic click — often on the trash zone (outside the bar) or on a chip
    // that's about to unmount. Swallow the next click anywhere, in capture, so no chip <a> navigates.
    const swallowClick = e => {
      e.preventDefault();
      e.stopPropagation();
      window.removeEventListener('click', swallowClick, true);
    };
    window.addEventListener('click', swallowClick, true);
    setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);

    if (over?.id === NAV_TRASH_ID) removeShortcut(active.id);
    else if (over) reorderShortcuts(active.id, over.id);
  };

  return (
    <>
      <div className='admin-nav-header'>
        <button
          ref={logoButtonRef}
          className={cn('nav-logo', { active: isDrawerOpen })}
          onClick={() => setIsDrawerOpen(open => !open)}
          type='button'
        >
          <FontAwesomeIcon icon={faThLarge} className='logo-icon' />
          <span className='logo-text'>ShopMy Admin</span>
        </button>

        {shortcutItems.length > 0 && <div className='nav-divider' />}

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          modifiers={[restrictToHorizontalAxis]}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={() => setIsDragging(true)}
          onDragCancel={() => setIsDragging(false)}
          onDragEnd={handleDragEnd}
        >
          <div className='nav-shortcuts'>
            <SortableContext items={shortcutKeys} strategy={horizontalListSortingStrategy}>
              {shortcutItems.map(item => (
                <AdminNavShortcut key={item.key} item={item} isActive={item.key === activeKey} canDrag={!isTouch} />
              ))}
            </SortableContext>
          </div>
          {isDragging && <AdminNavTrash />}
        </DndContext>

        <a href='/logout' className='nav-logout'>
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Log Out</span>
        </a>
      </div>

      {isDrawerOpen && (
        <AdminNavDrawer
          onClose={closeDrawer}
          items={visibleItems}
          pinnedKeys={pinnedKeys}
          recentKeys={recentKeys}
          activeKey={activeKey}
          onTogglePin={togglePin}
          onRestoreClassicMenu={restoreClassicMenu}
        />
      )}
    </>
  );
};

export default AdminNav;
